import React, { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import {
  StatCardActive,
  StatCardInTransit,
  StatCardArriving,
  StatCardDischarged,
  StatCardTransitCosts,
} from '../components/claude/StatCards';
import {
  UpcomingContainersCard,
  VesselDeparturesCard,
  BookingsCard,
  CurrentPricesCard,
  TransitTimesCard,
  LatestOffersCard,
} from '../components/claude/DashboardCards';
import { useExtendedStatistics } from '../hooks/useExtendedStatistics';
import { useUpcomingContainers } from '../hooks/useUpcomingContainers';
import { useBookings } from '../hooks/useBookings';
import { usePortTransitTimes } from '../hooks/usePortTransitTimes';
import { useLatestOffers } from '../hooks/useLatestOffers';
import { useCurrentOfferPrices } from '../hooks/useCurrentOfferPrices';
import { useVesselDepartures } from '../hooks/useVesselDepartures';
import { EMPTY_VESSEL_DEPARTURES } from '../interfaces/vesselDepartures.interface';
import { EMPTY_EXTENDED_STATISTICS } from '../interfaces/extendedStatistics.interface';
import { EMPTY_UPCOMING_CONTAINERS } from '../interfaces/upcomingContainers.interface';
import { EMPTY_BOOKINGS_SUMMARY } from '../interfaces/bookings.interface';
import { EMPTY_PORT_TRANSIT_TIMES } from '../interfaces/portTransitTimes.interface';
import { EMPTY_LATEST_OFFERS } from '../interfaces/latestOffers.interface';

const Index: React.FC = () => {
  const { user } = useAuth();
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';

  const { data: extStats, isLoading: extLoading } = useExtendedStatistics();
  const stats = extStats || EMPTY_EXTENDED_STATISTICS;

  const { data: upcomingData, isLoading: upcomingLoading } = useUpcomingContainers();
  const upcoming = upcomingData || EMPTY_UPCOMING_CONTAINERS;

  const { data: bookingsData, isLoading: bookingsLoading } = useBookings();
  const bookings = bookingsData || EMPTY_BOOKINGS_SUMMARY;

  const { data: transitData, isLoading: transitLoading } = usePortTransitTimes();
  const transit = transitData || EMPTY_PORT_TRANSIT_TIMES;

  const { data: offersData, isLoading: offersLoading } = useLatestOffers();
  const offers = offersData || EMPTY_LATEST_OFFERS;

  const { data: currentPrices, isLoading: currentPricesLoading } = useCurrentOfferPrices();

  const { data: vesselDeparturesData, isLoading: vesselDeparturesLoading } = useVesselDepartures();
  const vesselDepartures = vesselDeparturesData || EMPTY_VESSEL_DEPARTURES;

  // Aplică background-ul claude-design pe .content cât timp dashboard-ul e montat.
  // Regula `body.dashboard-claude-bg .content` e definită în claude-design.css.
  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  const firstName = user?.fullName?.split(' ')[0] || (isRo ? 'utilizator' : 'there');
  const welcome = isRo ? 'Bună ziua' : 'Good day';
  const subtitle = isRo
    ? 'Iată o privire de ansamblu asupra operațiunilor tale.'
    : "Here's an overview of your operations.";

  return (
    <div style={{ color: 'var(--text)' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>
          {welcome}, {firstName}
        </h1>
        <div className="muted fs-13 mt-1">{subtitle}</div>
      </div>

      <div className="stat-grid">
        <StatCardActive data={stats.active} isLoading={extLoading} />
        <StatCardInTransit data={stats.inTransit} isLoading={extLoading} />
        <StatCardArriving data={stats.arrivingSoon} isLoading={extLoading} />
        {isExport ? (
          <StatCardDischarged data={stats.discharged} isLoading={extLoading} />
        ) : (
          <StatCardTransitCosts
            data={stats.transitCosts ?? EMPTY_EXTENDED_STATISTICS.transitCosts}
            isLoading={extLoading}
          />
        )}
      </div>

      <div className="dash-grid">
        <UpcomingContainersCard data={upcoming} isLoading={upcomingLoading} />
        <VesselDeparturesCard data={vesselDepartures} isLoading={vesselDeparturesLoading} />

        <BookingsCard data={bookings} isLoading={bookingsLoading} />
        <CurrentPricesCard data={currentPrices} isLoading={currentPricesLoading} />

        <div className="span-2">
          <TransitTimesCard data={transit} isLoading={transitLoading} />
        </div>
        <LatestOffersCard data={offers} isLoading={offersLoading} />
      </div>
    </div>
  );
};

export default Index;
